import { useEffect, useState } from "react";
import { Menu, X, Puzzle, Phone } from "lucide-react";
import { scrollToId } from "@/components/Shared";

const LINKS = [
  { label: "Home", href: "#hero", testId: "nav-home" },
  { label: "About Us", href: "#about", testId: "nav-about" },
  { label: "Programs & Services", href: "#services", testId: "nav-services" },
  { label: "Enrollment", href: "#enrollment", testId: "nav-enrollment" },
  { label: "Scholarships", href: "#scholarships", testId: "nav-scholarships" },
  { label: "Parent Information", href: "#parent-info", testId: "nav-parent-info" },
  { label: "Contact Us", href: "#contact", testId: "nav-contact" },
];

const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    scrollToId(href);
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-sand/85 backdrop-blur-xl border-b border-amber-900/10 shadow-[0_8px_30px_-18px_rgba(15,76,92,0.35)]"
          : "bg-transparent"
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-5 sm:px-8 py-3.5">
        <button
          data-testid="nav-logo"
          onClick={() => go("#hero")}
          className="flex items-center gap-3 text-left"
        >
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-deepteal text-amber-200">
            <Puzzle className="h-5.5 w-5.5 h-5 w-5" strokeWidth={1.8} />
          </div>
          <div>
            <p className="font-serif text-[15px] sm:text-base font-semibold leading-tight text-deepteal">
              Pieces to Our Puzzle
            </p>
            <p className="font-mono text-[9px] tracking-[0.3em] uppercase text-amber-700">
              Childcare · Halethorpe MD
            </p>
          </div>
        </button>

        <nav className="hidden lg:flex items-center gap-6" aria-label="Primary">
          {LINKS.map((l) => (
            <button
              key={l.href}
              data-testid={l.testId}
              onClick={() => go(l.href)}
              className="text-[13px] font-semibold text-slate-700 hover:text-terracotta transition-colors duration-300"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            data-testid="nav-phone"
            href="tel:+14109003947"
            className="hidden xl:flex items-center gap-2 font-mono text-xs font-semibold text-deepteal hover:text-terracotta transition-colors"
          >
            <Phone className="h-3.5 w-3.5" /> (410) 900-3947
          </a>
          <button
            data-testid="nav-cta-enroll"
            onClick={() => go("#enrollment")}
            className="hidden sm:inline-flex items-center rounded-full bg-terracotta px-5 py-2.5 text-[13px] font-bold text-amber-50 shadow-lg shadow-terracotta/25 transition-all duration-300 hover:-translate-y-0.5 hover:bg-deepteal"
          >
            Enroll Your Child
          </button>
          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen(!open)}
            className="lg:hidden flex h-10 w-10 items-center justify-center rounded-xl border border-amber-900/15 bg-white/70 text-deepteal"
            aria-label="Toggle menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div data-testid="nav-mobile-menu" className="lg:hidden border-t border-amber-900/10 bg-sand/95 backdrop-blur-xl px-5 py-4">
          <div className="flex flex-col gap-1">
            {LINKS.map((l) => (
              <button
                key={l.href}
                data-testid={`${l.testId}-mobile`}
                onClick={() => go(l.href)}
                className="rounded-lg px-3 py-3 text-left text-sm font-semibold text-slate-700 hover:bg-amber-100/70 hover:text-terracotta transition-colors"
              >
                {l.label}
              </button>
            ))}
            <button
              data-testid="nav-cta-enroll-mobile"
              onClick={() => go("#enrollment")}
              className="mt-2 rounded-full bg-terracotta px-5 py-3 text-sm font-bold text-amber-50"
            >
              Enroll Your Child
            </button>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
